import { useConnection } from "@solana/wallet-adapter-react";
import { apyToApr } from "@stepfinance/common";
import { SolendPositionFetcher } from "@stepfinance/lending";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import type { YieldDataMap } from "../../components/dashboard/Balances/YieldBearingAssets";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import { useRefreshContext } from "../../contexts/refresh";
import { useLendingVaultsQuery } from "../queries/useLendingVaultsQuery";

// If yield bearing assets loads faster than the solend hook, the effect is
// cTokens teleporting from the Spot Assets section to the Yield Bearing assets
// section. This hardcoded list will prevent this from happening to all but the
// latest tokens
const KNOWN_CTOKEN_MINTS: string[] = [
  "4CxGuD2NMr6zM8f18gr6kRhgd748pnmkAhkY1YJtkup1",
  "D3Cu5urZJhkKyNZQQq2ne6xSfzbXLU4RrywVErMA2vf8",
  "DiMx1n2dJmxqFtENRPhYWsqi8Mhg2p39MpTzsm6phzMP",
  "BsWLxf6hRJnyytKR52kKBiz7qU7BB3SH77mrBxNnYU1G",
  "nZtL8HKX3aQtk3VpdvtdwPpXF2uMia522Pnan2meqdf",
  "993dVFL2uXWYeoXuEBFXR4BijeXdTv4s6BzsCjJZuwqk",
  "Bpm2aBL57uqVhgxutfRVrbtnjDpZLV8PZrRrytV1LgeT",
  "FbKvdbx5h6F86h1pZuEqv7FxwmsVhJ88cDuSqHvLm6Xf",
  "3JFC4cB56Er45nWVe29Bhnn5GnwQzSmHVf6eUq9ac91h",
  "BTsbZDV7aCMRJ3VNy9ygV4Q2UeEo9GpR8D6VvmMZzNr8",
  "2d95ZC8L5XP6xCnaKx8D5U5eX6rKbboBBAwuBLxaFmmJ",
  "Gqu3TFmJXfnfSX84kqbZ5u9JjSBVoesaHjfTsaPjRSnZ",
  "QQ6WK86aUCBvNPkGeYBKikk15sUg6aMUEi5PTL6eB4i",
  "E9LAZYxBVhJr9Cdfi9Tn4GSiJHDWSZDsew5tfgJja6Cu",
  "6XrbsKScacEwpEW5DVNko9t5vW3cim9wktAeT9mmiYHS",
  "5h6ssFpeDeRbzsEHDbTQNH7nVGgsKrZydxdSTnLm6QdV",
  "A38TjtcYrfutXT6nfRxhqwoGiXyzwJsGPmekoZYYmfgP",
  "AFq1sSdevxfqWGcmcz7XpPbfjHevcJY7baZf9RkyrzoR",
  "AppJPZka33cu4DyUenFe9Dc1ZmZ3oQju6mBn9k37bNAa",
  "4icXEpFVMrcqob6fnd3jZ6KjKrc6cqre6do1f8kKAC1u",
];

export const useSolend = () => {
  const { marketsInitialised, priceInUSD } = useMarkets();
  const { publicKey } = useEnrichedWallet();
  const { connection } = useConnection();
  const { saberPools } = useLiquidity();
  const { refreshID } = useRefreshContext();

  const { data: lendingData } = useLendingVaultsQuery();

  const fetcher = useMemo(() => {
    if (!publicKey || !lendingData?.solendData) {
      return undefined;
    }
    return new SolendPositionFetcher(publicKey, connection, lendingData.solendData, saberPools);
  }, [connection, lendingData, publicKey, saberPools]);

  const { data: positions, refetch } = useQuery(
    ["solend", publicKey?.toBase58()],
    async () => {
      if (!fetcher) {
        return null;
      }
      const positions = await fetcher.fetchPositions(priceInUSD);
      const { mainReserveStats, collateralPositionMap, isolatedPoolsPositionMap } = positions;
      const yieldDataMap: YieldDataMap = mainReserveStats.reduce((map, value) => {
        map[value.collateralMintAddress] = {
          apr: apyToApr(value.supplyInterestAPY * 100),
          xPerToken: value.cTokenExchangeRate,
          yieldingSymbol: value.symbol,
        };

        return map;
      }, {});

      const solendCTokenMints: string[] = mainReserveStats.map((stats) => stats.collateralMintAddress);

      return {
        collateral: collateralPositionMap,
        isolatedPools: isolatedPoolsPositionMap,
        mainReserveStats,
        yieldDataMap,
        solendCTokenMints,
      };
    },
    {
      retry: false,
      enabled: marketsInitialised && !!fetcher,
    },
  );

  const {
    collateral = {},
    isolatedPools = {},
    mainReserveStats = [],
    solendCTokenMints = KNOWN_CTOKEN_MINTS,
    yieldDataMap = {},
  } = positions ?? {};

  useEffect(() => {
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshID]);

  return {
    yieldFarms: {},
    collateral,
    isolatedPools,
    mainReserveStats,
    solendCTokenMints,
    yieldDataMap,
  };
};
